import React, { useState, useRef } from 'react';
import { StyledForms } from '@app/components/styled/form/Form';
import { TextInput, NumberInput, Button } from '@app/components';
import { PriceInput } from '@app/components/form/PriceInput';
import { useFormContext } from 'react-hook-form';
import { DeliveryZoneMapModal } from './DeliveryZoneMapModal';
import { View, Text, StyleSheet } from 'react-native';
import { theme } from '@app/theme';
import { parseKmlToGeoJson } from './kml-utils';
import { useToast } from '@app/hooks';
import { deliveryZoneApi } from '@app/store';

export const DeliveryZoneForm = () => {
  const { watch, setValue } = useFormContext();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const location = watch('location');
  const currentId = watch('_id');

  // On récupère toutes les zones existantes du shop courant
  const { data: deliveryZonesResponse } = deliveryZoneApi.useReadManyDeliveryZonesQuery({
    pagination: {
      skip: 0,
      take: 1000,
    },
    filters: {},
    sort: ['name:ASC'],
  });

  const allZones: any[] = Array.isArray(deliveryZonesResponse)
    ? deliveryZonesResponse as any[]
    // @ts-ignore forme paginée éventuelle
    : (deliveryZonesResponse?.data ?? []);

  const existingZones = allZones
    .filter((z) => z?.location?.coordinates && z._id !== currentId)
    .map((z) => z.location);

  const handleKmlUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
          const coordinates = await parseKmlToGeoJson(file);
          if (coordinates) {
              setValue('location', {
                  type: 'Polygon',
                  coordinates: coordinates
              }, { shouldDirty: true, shouldValidate: true });
              toast.success('Fichier KML importé avec succès. Vous pouvez ajuster la zone manuellement.');
          } else {
              toast.error('Aucun polygone valide trouvé dans le fichier KML.');
          }
      } catch (error) {
          toast.error('Erreur lors de la lecture du fichier KML.');
          console.error(error);
      } finally {
          // Reset input
          if (fileInputRef.current) {
              fileInputRef.current.value = '';
          }
      }
  };

  const isZoneDefined = location?.coordinates && location.coordinates.length > 0;

  return (
    <StyledForms.Form>
      <TextInput
        name="name"
        label="Nom"
        autoCapitalize="none" />

      <PriceInput
        name="minPrice"
        label="Montant minimum de commande"
      />

      <PriceInput
        name="fees.amount"
        label="Frais de livraison"
      />

      <View style={styles.row}>
          <View style={styles.col}>
            <PriceInput
                name="fees.reducedThreshold"
                label="Seuil frais réduits"
            />
          </View>
          <View style={styles.col}>
            <PriceInput
                name="fees.reducedAmount"
                label="Montant réduit"
            />
          </View>
      </View>

      <NumberInput
        name="extraDeliveryTime"
        label="Temps de livraison supplémentaire"
        suffix="min"
      />

      <View style={styles.zoneContainer}>
        <View style={styles.zoneHeader}>
            <Text style={styles.zoneLabel}>Zone géographique</Text>
            <View style={[styles.badge, isZoneDefined ? styles.badgeSuccess : styles.badgeError]}>
                <Text style={[styles.badgeText, isZoneDefined ? styles.badgeTextSuccess : styles.badgeTextError]}>
                    {isZoneDefined ? 'Définie' : 'Non définie'}
                </Text>
            </View>
        </View>

        <View style={styles.actions}>
            <Button
                title={isZoneDefined ? "Ajuster" : "Tracer"}
                onPress={() => setIsMapOpen(true)}
                grow
            />
            
            <View style={{ width: 10 }} />

            <Button
                title="Import KML"
                onPress={() => fileInputRef.current?.click()}
                bordered
                grow
            />
            <input
                type="file"
                accept=".kml,.xml"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleKmlUpload}
            />
        </View>
        {isZoneDefined && (
            <Text style={styles.hint}>
                Zone importée. Cliquez sur "Ajuster" pour modifier.
            </Text>
        )}
      </View>

      {isMapOpen && (
        <DeliveryZoneMapModal
            isOpen={isMapOpen}
            onClose={() => setIsMapOpen(false)}
            initialData={location}
            existingZones={existingZones}
            onSave={(newLocation) => {
                setValue('location', newLocation, { shouldDirty: true, shouldValidate: true });
            }}
        />
      )}

    </StyledForms.Form>
  );
};

const styles = StyleSheet.create({
    zoneContainer: {
        marginVertical: 10,
        padding: 16,
        backgroundColor: 'rgba(240, 249 , 252, .04)',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.gainsboro,
    },
    zoneHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    zoneLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.babypowder,
    },
    badge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
        borderWidth: 1,
    },
    badgeSuccess: {
        backgroundColor: 'rgba(46, 204, 113, 0.1)',
        borderColor: '#2ecc71',
    },
    badgeError: {
        backgroundColor: 'rgba(231, 76, 60, 0.1)',
        borderColor: '#e74c3c',
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '600',
    },
    badgeTextSuccess: {
        color: '#2ecc71',
    },
    badgeTextError: {
        color: '#e74c3c',
    },
    actions: {
        flexDirection: 'row',
    },
    row: {
        flexDirection: 'row',
        gap: 15,
    },
    col: {
        flex: 1
    },
    hint: {
        marginTop: 12,
        fontSize: 13,
        color: theme.colors.babypowder,
        opacity: 0.7,
        fontStyle: 'italic',
        textAlign: 'center'
    }
});
